import { ChangeEvent } from 'react';
import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid, Stack } from '@mui/material';
import { PoolButton, PoolModal, PoolTextField, PoolTypography } from '@synergy/components';
import { fields, nullIfEmptyLocalizedString } from '@synergy/utils';

import * as TaxRateActions from './store/tax-rates-actions';
import { ConfigurationTranslationsField } from '../../components/configuration-translations-field';
import { TaxRate } from '../../../../common/models/tax-rate';
import { TaxRateFormikValues } from './models/tax-rate-formik-values';
import { taxRatesModalValidationSchema } from './tax-rates-detail-modal-validation-schema';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface TaxRateDetailModalProps {
    visible: boolean;
    taxRate: TaxRate | undefined;
    onClose: () => void;
}

export const TaxRateDetailModal = (props: TaxRateDetailModalProps) => {
    const dispatch = useThunkDispatch();
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const fieldNames = fields<TaxRateFormikValues>();

    const gridItemSize = 3;

    const handleSubmit = (values: TaxRateFormikValues, formikProps: FormikHelpers<TaxRateFormikValues>) => {
        const taxRate: TaxRate = {
            id: props.taxRate?.id ?? -1,
            description: values.description,
            invoiceText: nullIfEmptyLocalizedString(values.invoiceText),
            taxCode: getNumber(values.taxCode),
            counterAccount: getNumber(values.counterAccount),
            taxPercentage: getNumber(values.taxPercentage),
            branchNumber: getNumber(values.branchNumber),
        };

        if (taxRate.id !== -1) {
            dispatch(TaxRateActions.updateTaxRate(taxRate));
        } else {
            dispatch(TaxRateActions.createTaxRate(taxRate));
        }

        handleClose(formikProps);
    };

    const getNumber = (value?: string | number | null): number | null => {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        return +value;
    };

    const handleClose = (formikProps: FormikHelpers<TaxRateFormikValues>) => {
        setTimeout(() => formikProps.resetForm(), 300);
        props.onClose();
    };

    const initialValues: TaxRateFormikValues = {
        description: { [currentLocale]: '', ...props.taxRate?.description },
        invoiceText: { [currentLocale]: '', ...props.taxRate?.invoiceText },
        taxCode: props.taxRate?.taxCode ?? null,
        counterAccount: props.taxRate?.counterAccount ?? null,
        taxPercentage: props.taxRate?.taxPercentage ?? (props.taxRate?.id ? null : 0),
        branchNumber: props.taxRate?.branchNumber ?? null,
    };

    const actionChildren = (formikProps: FormikProps<TaxRateFormikValues>) => (
        <>
            <PoolButton key="1" styleType="firstLevel" variant="text" onClick={() => handleClose(formikProps)}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>
            <PoolButton key="2" styleType="firstLevel" onClick={() => formikProps.submitForm()}>
                <FormattedMessage id={props.taxRate?.id ? 'app.common.save' : 'app.common.add'} />
            </PoolButton>
        </>
    );

    const handleCounterAccountChanged =
        (formikProps: FormikProps<TaxRateFormikValues>) => (event: ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            const parsedValue = value ? +value : null;
            formikProps.setFieldValue(fieldNames.counterAccount, parsedValue);
        };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validationSchema={taxRatesModalValidationSchema(currentLocale)}
        >
            {(formikProps: FormikProps<TaxRateFormikValues>) => (
                <Form>
                    <PoolModal
                        title={
                            <FormattedMessage
                                id={
                                    props.taxRate?.id
                                        ? 'app.configuration.taxRates.edit'
                                        : 'app.configuration.createTaxRate'
                                }
                            />
                        }
                        isVisible={props.visible}
                        handleClose={() => handleClose(formikProps)}
                        actionChildren={actionChildren(formikProps)}
                        width="lg"
                    >
                        <Stack spacing={2}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <PoolTypography styleType="sectionTitleNew">
                                        <FormattedMessage id={'app.common.details'} />
                                    </PoolTypography>
                                </Grid>
                                <Grid item={true} xs={gridItemSize}>
                                    <Field name={fieldNames.counterAccount}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                {...field}
                                                value={field.value ?? ''}
                                                onChange={handleCounterAccountChanged(formikProps)}
                                                fullWidth={true}
                                                type="number"
                                                label={<FormattedMessage id={'app.common.counterAccount'} />}
                                                error={meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                                autoFocus={true}
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={gridItemSize}>
                                    <Field name={fieldNames.taxCode}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                {...field}
                                                value={field.value ?? ''}
                                                fullWidth={true}
                                                label={<FormattedMessage id={'app.common.taxCode'} />}
                                                type="number"
                                                error={meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={gridItemSize}>
                                    <Field name={fieldNames.taxPercentage}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                {...field}
                                                value={field.value ?? ''}
                                                fullWidth={true}
                                                type="number"
                                                label={
                                                    <FormattedMessage
                                                        id={'app.configuration.taxRates.taxRatePercentage'}
                                                    />
                                                }
                                                error={meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={gridItemSize}>
                                    <Field name={fieldNames.branchNumber}>
                                        {({ field, meta }: FieldProps<number>) => (
                                            <PoolTextField
                                                {...field}
                                                value={field.value ?? ''}
                                                fullWidth={true}
                                                type="number"
                                                label={
                                                    <FormattedMessage id={'app.configuration.taxRates.branchNumber'} />
                                                }
                                                error={meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : null
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <PoolTypography styleType="sectionTitleNew">
                                        <FormattedMessage id={'app.label.description'} />
                                    </PoolTypography>
                                </Grid>
                                {availableLocales.map(locale => {
                                    return (
                                        <Grid key={locale} item={true} xs={12}>
                                            <ConfigurationTranslationsField
                                                locale={locale}
                                                fieldName={fieldNames.description}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <PoolTypography styleType="sectionTitleNew">
                                        <FormattedMessage id={'app.configuration.taxRates.invoiceText'} />
                                    </PoolTypography>
                                </Grid>
                                {availableLocales.map(locale => {
                                    return (
                                        <Grid key={locale} item={true} xs={12}>
                                            <ConfigurationTranslationsField
                                                locale={locale}
                                                fieldName={fieldNames.invoiceText}
                                                required={false}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Stack>
                    </PoolModal>
                </Form>
            )}
        </Formik>
    );
};
